/**
 * 环境变量工具
 */

export const env = {
  // 开发环境标识
  isDev: import.meta.env.VITE_DEV === 'true',

  // 时区
  timeZone: import.meta.env.VITE_TIME_ZONE || 'Asia/Manila',

  // API 相关
  baseUrl: import.meta.env.VITE_BASE_URL || '',
  baseUrlWs: import.meta.env.VITE_BASE_URL_WS || '',
  apiUrl: import.meta.env.VITE_API_URL || '/api',

  // 货币格式
  currencyFormat: import.meta.env.VITE_CURRENCY_FORMAT || 'BR',

  // 文件上传相关
  uploadType: import.meta.env.VITE_UPLOAD_TYPE || 'server',
  uploadUrl: import.meta.env.VITE_UPLOAD_URL || '/',
  imgUrl: import.meta.env.VITE_IMG_URL || '',

  // 构建相关
  basePath: import.meta.env.VITE_BASE_PATH || '/',
  outDir: import.meta.env.VITE_OUT_DIR || 'dist',

  // 调试相关
  dropDebugger: import.meta.env.VITE_DROP_DEBUGGER === 'true',
  dropConsole: import.meta.env.VITE_DROP_CONSOLE === 'true',
  sourcemap: import.meta.env.VITE_SOURCEMAP === 'true',
}

/**
 * 获取完整的API URL
 */
export function getApiUrl(path: string = ''): string {
  return `${env.baseUrl}${env.apiUrl}${path}`
}

/**
 * 获取完整的图片URL
 */
export function getImgUrl(path: string = ''): string {
  return `${env.imgUrl}${path}`
}

/**
 * 获取WebSocket URL，动态替换域名
 */
export function getWsUrl(path: string = ''): string {
  const baseUrl = env.baseUrlWs

  if (!baseUrl) {
    return ''
  }

  // 调试信息
  console.log('WebSocket URL 调试信息:', {
    baseUrl,
    path,
    startsWithAtHttp: baseUrl.startsWith('@http://'),
    startsWithAtHttps: baseUrl.startsWith('@https://')
  })

  if (typeof window === 'undefined') {
    return `${baseUrl}${path}`
  }

  // 通用处理：如果 baseUrl 是 http:// 开头的 IP 地址格式，直接转换为 ws://
  if (baseUrl.startsWith('http://')) {
    // 匹配 IP 地址格式（如 192.168.1.210:8080），支持可选的路径部分
    const ipPortMatch = baseUrl.match(/^http:\/\/(\d+\.\d+\.\d+\.\d+)(?::(\d+))?(?:\/|$)/)
    if (ipPortMatch) {
      const ip = ipPortMatch[1]
      const port = ipPortMatch[2] || '8080' // 如果没有端口，默认使用8080
      console.log('匹配到 IP 地址格式，转换为 WebSocket:', { ip, port, path })
      return `ws://${ip}:${port}${path}`
    }
  }

  // 特殊判断：如果是192.168.1.61:8080或192.168.1.210:8080，转换为ws协议（保留兼容性）
  if (baseUrl.includes('192.168.1.61:8080')) {
    // 如果是http协议，转换为ws协议
    if (baseUrl.startsWith('http://')) {
      return `ws://192.168.1.61:8080${path}`
    }
    return `${baseUrl}${path}`
  }

  if (baseUrl.includes('192.168.1.210:8080')) {
    // 如果是http协议，转换为ws协议
    if (baseUrl.startsWith('http://')) {
      return `ws://192.168.1.210:8080${path}`
    }
    return `${baseUrl}${path}`
  }

  if (baseUrl.includes('192.168.210:8080')) {
    // 如果是http协议，转换为ws协议
    if (baseUrl.startsWith('http://')) {
      return `ws://192.168.210:8080${path}`
    }
    return `${baseUrl}${path}`
  }

  // 特殊处理：如果是 @http:// 或 @https:// 格式，提取域名并构建 websocket URL
  if (baseUrl.startsWith('@http://') || baseUrl.startsWith('@https://')) {
    const urlMatch = baseUrl.match(/@https?:\/\/([^\/]+)/)
    if (urlMatch) {
      const extractedDomain = urlMatch[1]

      // 检查是否是IP地址格式
      const ipMatch = extractedDomain.match(/^(\d+\.\d+\.\d+\.\d+)(?::(\d+))?$/)
      if (ipMatch) {
        // 如果是IP地址，使用ws协议，固定使用8080端口
        const ip = ipMatch[1]
        return `ws://${ip}:8080${path}`
      }

      // 检查域名格式并处理
      const domainParts = extractedDomain.split('.')

      // 如果域名有3节以上，去掉第一级子域名
      if (domainParts.length >= 3) {
        // 去掉第一级子域名，保留后面部分
        const rootDomain = domainParts.slice(1).join('.')
        return `wss://websocket.${rootDomain}${path}`
      } else {
        // 2节域名，添加 .com 后缀
        let rootDomain = extractedDomain
        if (!rootDomain.endsWith('.com')) {
          rootDomain = `${rootDomain}.com`
        }
        return `wss://websocket.${rootDomain}${path}`
      }
    }
  }

  // 处理其他格式的WebSocket URL（如 wss:// 或 ws:// 格式）
  // 先检查是否是 IP 地址格式，如果是则不应该执行域名替换逻辑
  const ipCheckMatch = baseUrl.match(/^(https?:\/\/)?(\d+\.\d+\.\d+\.\d+)(?::(\d+))?/)
  if (ipCheckMatch) {
    // 如果是 IP 地址格式，直接返回，不执行域名替换
    const ip = ipCheckMatch[2]
    const port = ipCheckMatch[3] || '8080'
    const protocol = baseUrl.startsWith('https://') ? 'wss://' : 'ws://'
    console.log('检测到 IP 地址格式，直接返回:', { ip, port, protocol, path })
    return `${protocol}${ip}:${port}${path}`
  }

  // 获取当前域名，并去掉 www 前缀
  let currentHostname = window.location.hostname
  if (currentHostname.startsWith('www.')) {
    currentHostname = currentHostname.substring(4) // 去掉 'www.'
  }

  // 如果域名有3节以上，只取最后两节
  const currentHostnameParts = currentHostname.split('.')
  if (currentHostnameParts.length >= 3) {
    currentHostname = currentHostnameParts.slice(-2).join('.')
  }

  // 提取环境变量中的域名
  const urlMatch = baseUrl.match(/^(wss?:\/\/)?([^\/]+)/)
  if (!urlMatch) {
    return `${baseUrl}${path}`
  }

  const envHostname = urlMatch[2]

  // 去掉环境变量域名中的 www 前缀进行比较
  let envHostnameWithoutWww = envHostname
  if (envHostnameWithoutWww.startsWith('www.')) {
    envHostnameWithoutWww = envHostnameWithoutWww.substring(4)
  }

  // 如果环境变量域名也有3节以上，只取最后两节（但如果是 IP 地址，不应该执行这个逻辑）
  const envHostnameParts = envHostnameWithoutWww.split('.')
  if (envHostnameParts.length >= 3) {
    // 检查是否是 IP 地址（IP 地址的每个部分都是数字）
    const isIpAddress = envHostnameParts.every(part => {
      const numPart = part.split(':')[0] // 去掉端口号部分
      return /^\d+$/.test(numPart)
    })
    if (!isIpAddress) {
      envHostnameWithoutWww = envHostnameParts.slice(-2).join('.')
    }
  }

  // 如果当前域名和环境变量中的域名相同，使用环境变量的完整配置
  if (currentHostname === envHostnameWithoutWww) {
    return `${baseUrl}${path}`
  }

  // 如果域名不同，只替换域名部分，保持其他格式不变
  let dynamicUrl = baseUrl.replace(envHostname, currentHostname)

  // 如果环境变量有 websocket. 前缀，但替换后没有，需要添加
  if (baseUrl.includes('websocket.') && !dynamicUrl.includes('websocket.')) {
    dynamicUrl = dynamicUrl.replace(`://${currentHostname}`, `://websocket.${currentHostname}`)
  }

  return `${dynamicUrl}${path}`
}
